/* eslint-disable max-lines */
import React, { useRef } from 'react';
import { Draggable, DraggableProvided } from 'react-beautiful-dnd';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { CanDisable } from '@modernloop/shared/helper-components';
import { OrderLockIcon, OrderShuffleIcon } from '@modernloop/shared/icons';
import { Box, Divider, IconButton, Paper, Stack, TextField, Theme, Typography, inputBaseClasses } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import {
  DateTimeRangeInput,
  DurationInput,
  InterviewGroup_JobFragment,
  InterviewGroup_JobStageInterviewGroupFragment,
  InterviewGroup_JobStageInterviewGroupsFragment,
  InterviewGroup_JobStageInterviewsFragment,
  InterviewGroup_SchedulingWindowFragment,
} from 'src/generated/mloop-graphql';

import { isBreak } from 'src/utils/interview';

import { DEBOUNCE_TIMEOUT } from 'src/constants';

import InterviewGroupMenu, { InterviewGroupOptions } from './InterviewGroupMenu';
import InterviewGroupOld from './InterviewGroupOld';
import JobStageInterview from './JobStageInterview';
import { InterviewMenuOptions } from './MoreMenuOptions';

type Props = {
  count: number;
  schedulable: boolean;
  readonly?: boolean;
  candidateAvailabilities?: DateTimeRangeInput[];
  minSchedulingWindowSecondsNeeded: number;
  onUpdated: (
    jobStageInterviewGroup: InterviewGroup_JobStageInterviewGroupFragment,
    schedulingWindowDuration?: DurationInput
  ) => void;
  onDelete: (jobStageInterviewGroupId: string) => void;
  onMoveJobStageInterviewGroup: (groupId: string, position: number) => void;
  onGroupJobStageInterviewGroup: (startGroupIndex: number, endGroupIndex: number) => void;
  onUngroupJobStageInterviewGroup: (groupId: string) => void;
  atsSyncedInterviewFields?: boolean;

  // For Draggable
  index: number;
};

type Fragments = {
  job: InterviewGroup_JobFragment;
  jobStageInterviewGroup: InterviewGroup_JobStageInterviewGroupFragment;
  jobStageInterviewGroups: InterviewGroup_JobStageInterviewGroupsFragment[];
  jobStageInterviews: InterviewGroup_JobStageInterviewsFragment[];
  schedulingWindow: InterviewGroup_SchedulingWindowFragment | undefined;
};

const InterviewGroup: FCWithFragments<Fragments, Props> = ({
  job,
  jobStageInterviewGroup,
  jobStageInterviewGroups,
  jobStageInterviews,
  schedulingWindow,
  count,
  index,
  schedulable,
  readonly,
  candidateAvailabilities,
  minSchedulingWindowSecondsNeeded,
  onUpdated,
  onDelete,
  onMoveJobStageInterviewGroup,
  onGroupJobStageInterviewGroup,
  onUngroupJobStageInterviewGroup,
  atsSyncedInterviewFields = false,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const breakImprovementsEnabled = useFlag('org_break_improvements');
  const { locked, name } = jobStageInterviewGroup;
  const jobStageInterviewIds: string[] =
    jobStageInterviewGroup.jobStageInterviews?.map((interview) => interview.id) || [];
  const interviewGroupNameRef = useRef<HTMLInputElement>(null);

  const debouncedOnUpdated = useDebouncedCallback(onUpdated, DEBOUNCE_TIMEOUT);

  const handleNameInputOnBlur = () => {
    if (!name) {
      onUpdated({ ...jobStageInterviewGroup, name: 'Group' });
    }
  };

  const handleInterviewGroupNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncedOnUpdated({ ...jobStageInterviewGroup, name: event.target.value });
  };

  const handlePlanOrderClick = () => {
    onUpdated({ ...jobStageInterviewGroup, locked: !locked });
  };

  const handleMenuSelect = (type: InterviewGroupOptions) => {
    switch (type) {
      case InterviewGroupOptions.MOVE_UP: {
        onMoveJobStageInterviewGroup(jobStageInterviewGroup.id, index - 2);
        break;
      }
      case InterviewGroupOptions.MOVE_DOWN: {
        onMoveJobStageInterviewGroup(jobStageInterviewGroup.id, index + 2);
        break;
      }
      case InterviewGroupOptions.GROUP_WITH_BELOW: {
        // When grouping interview we want to group the interview and break below hence index + 2
        onGroupJobStageInterviewGroup(index, index + 2);
        break;
      }
      case InterviewGroupOptions.GROUP_WITH_ABOVE: {
        // When grouping interview we want to group the interview and break above hence index - 2
        onGroupJobStageInterviewGroup(index, index - 2);
        break;
      }
      case InterviewGroupOptions.UNGROUP: {
        onUngroupJobStageInterviewGroup(jobStageInterviewGroup.id);
        break;
      }
      case InterviewGroupOptions.RENAME: {
        setTimeout(() => interviewGroupNameRef?.current?.focus(), 1); // without a delay here, the call to .focus() doesn't work
        break;
      }
      case InterviewGroupOptions.DELETE: {
        onDelete(jobStageInterviewGroup.id);
        break;
      }
      default:
    }
  };

  const handleInterviewMenuSelect = (jobStageInterviewId: string, option: InterviewMenuOptions) => {
    switch (option) {
      case InterviewMenuOptions.MOVE_UP: {
        // If there is only 1 interview or 1 break in the group
        if (jobStageInterviewGroup.jobStageInterviews?.length === 1) {
          onMoveJobStageInterviewGroup(jobStageInterviewGroup.id, index - 2);
        } else {
          const interviews = [...(jobStageInterviewGroup.jobStageInterviews || [])];
          const interviewIndex = interviews.findIndex((interview) => interview.id === jobStageInterviewId);

          // When moving up we want to swap the interview with the one above it and leave the break in between in same position
          if (interviewIndex - 2 >= 0 && interviewIndex - 2 < interviews.length) {
            const tempInterview = interviews[interviewIndex - 2];
            interviews[interviewIndex - 2] = interviews[interviewIndex];
            interviews[interviewIndex] = tempInterview;
          }

          onUpdated({ ...jobStageInterviewGroup, jobStageInterviews: interviews });
        }
        break;
      }
      case InterviewMenuOptions.MOVE_DOWN: {
        // If there is only 1 interview or 1 break in the group
        if (jobStageInterviewGroup.jobStageInterviews?.length === 1) {
          onMoveJobStageInterviewGroup(jobStageInterviewGroup.id, index + 2);
        } else {
          const interviews = [...(jobStageInterviewGroup.jobStageInterviews || [])];
          const interviewIndex = interviews.findIndex((interview) => interview.id === jobStageInterviewId);

          // When moving down we want to swap the interview with the one below it and leave the break in between in same position
          if (interviewIndex + 2 >= 0 && interviewIndex + 2 < interviews.length) {
            const tempInterview = interviews[interviewIndex + 2];
            interviews[interviewIndex + 2] = interviews[interviewIndex];
            interviews[interviewIndex] = tempInterview;
          }

          onUpdated({ ...jobStageInterviewGroup, jobStageInterviews: interviews });
        }
        break;
      }
      case InterviewMenuOptions.GROUP_WITH_ABOVE: {
        // When grouping interview we want to group the interview and break above hence index - 2
        onGroupJobStageInterviewGroup(index, index - 2);
        break;
      }
      case InterviewMenuOptions.GROUP_WITH_BELOW: {
        // When grouping interview we want to group the interview and break below hence index + 2
        onGroupJobStageInterviewGroup(index, index + 2);
        break;
      }
      default:
    }
  };

  if (!breakImprovementsEnabled) {
    return (
      <InterviewGroupOld
        job={job}
        jobStageInterviewGroup={jobStageInterviewGroup}
        jobStageInterviewGroups={jobStageInterviewGroups}
        jobStageInterviews={jobStageInterviews}
        count={count}
        index={index}
        schedulable={schedulable}
        readonly={readonly}
        candidateAvailabilities={candidateAvailabilities}
        minSchedulingWindowSecondsNeeded={minSchedulingWindowSecondsNeeded}
        onUpdated={onUpdated}
        onDelete={onDelete}
        onMoveJobStageInterviewGroup={onMoveJobStageInterviewGroup}
        onGroupJobStageInterviewGroup={onGroupJobStageInterviewGroup}
        onUngroupJobStageInterviewGroup={onUngroupJobStageInterviewGroup}
        atsSyncedInterviewFields={atsSyncedInterviewFields}
      />
    );
  }

  if (!jobStageInterviewGroup.jobStageInterviews?.length) {
    return null;
  }

  const separator = (
    <Box sx={{ m: '0 auto' }}>
      <Divider
        flexItem
        sx={{
          height: (theme) => theme.spacing(2.5),
          width: '1px',
          borderLeft: (theme) => `1px dashed ${theme.palette.borderState.default}`,
        }}
      />
    </Box>
  );

  const groupHasOnlyBreak =
    jobStageInterviewGroup.jobStageInterviews.length === 1 &&
    isBreak(jobStageInterviewGroup.jobStageInterviews[0].interviewType);

  return (
    <Draggable
      key={jobStageInterviewGroup.id}
      draggableId={jobStageInterviewGroup.id}
      index={index}
      isDragDisabled={readonly || groupHasOnlyBreak}
    >
      {(draggableProvided: DraggableProvided) => {
        const prevInterviewsCount = Math.floor(
          jobStageInterviewGroups
            .filter((g, i) => i < index)
            .map((g) => g.jobStageInterviews)
            .flat().length / 2 // Dividing by 2 to ignore the breaks.
        );

        const header =
          jobStageInterviewIds.length > 2 ? (
            <CanDisable disabled={Boolean(readonly)}>
              {jobStageInterviewIds.length > 2 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    backgroundColor: (theme: Theme) => theme.palette.background.paperHeader,
                    padding: (theme) => theme.spacing(1, 2.5),
                    margin: -1.5,
                    marginBottom: 0,
                  }}
                >
                  {locked ? <OrderLockIcon fontSize="small" /> : <OrderShuffleIcon fontSize="small" />}
                  <Typography variant="body2">
                    {jobStageInterviewGroup.locked
                      ? `This group will happen in positions ${prevInterviewsCount + 1} to ${Math.ceil(
                          prevInterviewsCount + jobStageInterviewIds.length / 2
                        )}`
                      : 'This group may swap positions'}
                  </Typography>
                </Stack>
              )}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{ mt: 0.5, mb: 1.5 }}
              >
                <Stack direction="row" alignItems="center" spacing={1} flexGrow={1}>
                  <IconButton onClick={handlePlanOrderClick}>
                    {locked && (
                      <OrderLockIcon tooltip={t('hiring-dashboard-schedule:schedule.interviewPlan.lockedPosition')} />
                    )}
                    {!locked && (
                      <OrderShuffleIcon
                        tooltip={t('hiring-dashboard-schedule:schedule.interviewPlan.shufflePosition')}
                      />
                    )}
                  </IconButton>
                  <TextField
                    defaultValue={name}
                    fullWidth
                    placeholder={t('hiring-dashboard-schedule:schedule.textField.groupPlaceholder')}
                    inputRef={interviewGroupNameRef}
                    inputProps={{ style: { fontWeight: 600, width: '100%', flex: 1 } }}
                    onChange={handleInterviewGroupNameChange}
                    onBlur={handleNameInputOnBlur}
                    sx={{
                      '& fieldset': { border: 'none' },
                      [`& .${inputBaseClasses.root}`]: {
                        backgroundColor: 'transparent',
                        border: '1px solid transparent',
                        '&:hover': { border: (theme) => `1px solid ${theme.palette.borderState.default}` },
                      },
                    }}
                  />
                </Stack>

                <InterviewGroupMenu count={count} index={index} onMenuSelect={handleMenuSelect} />
              </Stack>
            </CanDisable>
          ) : null;

        return (
          <Stack
            ref={draggableProvided.innerRef}
            {...draggableProvided.draggableProps}
            {...draggableProvided.dragHandleProps}
          >
            <Paper
              variant="outlined"
              sx={{
                padding: jobStageInterviewIds.length >= 2 ? 1.5 : 0,
                borderRadius: '12px',
                border: jobStageInterviewIds.length === 1 ? 'none' : undefined,
                backgroundColor: jobStageInterviewIds.length === 1 ? 'transparent' : undefined,
              }}
            >
              {header}
              <Stack>
                {jobStageInterviewGroup.jobStageInterviews?.map((jobStageInterview, i) => {
                  const linkableInterviews = jobStageInterviews.filter(
                    (interview) => interview.id !== jobStageInterview.id
                  );

                  if (!jobStageInterview) {
                    return null;
                  }

                  return [
                    <JobStageInterview
                      key={jobStageInterview.id}
                      job={job}
                      jobStageInterviewId={jobStageInterview.id}
                      jobStageInterview={jobStageInterview}
                      jobStageInterviewGroups={jobStageInterviewGroups}
                      jobStageInterviews={jobStageInterviews}
                      linkableInterviews={linkableInterviews}
                      schedulingWindow={schedulingWindow}
                      groupJobStageInterviewIds={jobStageInterviewIds}
                      index={i}
                      groupIndex={index}
                      schedulable={schedulable}
                      readonly={readonly}
                      candidateAvailabilities={candidateAvailabilities}
                      minSchedulingWindowSecondsNeeded={minSchedulingWindowSecondsNeeded}
                      atsSyncedInterviewFields={atsSyncedInterviewFields}
                      onUpdated={(newJobStageInterview, schedulingWindowDuration: DurationInput) => {
                        const updatedGroup: InterviewGroup_JobStageInterviewGroupFragment = {
                          ...jobStageInterviewGroup,
                          // The below check is to ensure if there is only one interview in the group,
                          // then the group locked value should be based on the interviews isLockedOrder value.
                          locked:
                            jobStageInterviewGroup?.jobStageInterviews &&
                            jobStageInterviewGroup.jobStageInterviews.length === 1
                              ? newJobStageInterview.isLockedOrder
                              : jobStageInterviewGroup.locked,
                          jobStageInterviews: jobStageInterviewGroup.jobStageInterviews?.map((interview) => {
                            if (interview.id === jobStageInterview.id) {
                              return newJobStageInterview;
                            }
                            return interview;
                          }),
                        };
                        onUpdated(updatedGroup, schedulingWindowDuration);
                      }}
                      onDelete={() => {
                        // If there is only 1 interview in the group then delete the entire group along with break.
                        if (jobStageInterviewGroup.jobStageInterviews?.length === 1) {
                          onDelete(jobStageInterviewGroup.id);
                        } else {
                          const jobStageInterviewsClone = [...(jobStageInterviewGroup.jobStageInterviews || [])];
                          if (i === jobStageInterviewsClone.length - 1) {
                            // If deleting the last interview in the group delete the break before it as well.
                            jobStageInterviewsClone.splice(i - 1, 2);
                          } else {
                            // If deleting any other interview in the group delete the break after it as well.
                            jobStageInterviewsClone.splice(i, 2);
                          }
                          onUpdated({
                            ...jobStageInterviewGroup,
                            // The below check is to ensure if there is only one interview in the group,
                            // then the group locked value should be based on the interviews isLockedOrder value.
                            locked:
                              jobStageInterviewsClone.length === 1
                                ? jobStageInterviewsClone[0].isLockedOrder
                                : jobStageInterviewGroup.locked,
                            jobStageInterviews: jobStageInterviewsClone,
                          });
                        }
                      }}
                      onMenuSelect={handleInterviewMenuSelect}
                    />,
                    i < jobStageInterviewIds.length - 1 ? separator : null,
                  ];
                })}
              </Stack>
            </Paper>

            {separator}
          </Stack>
        );
      }}
    </Draggable>
  );
};

InterviewGroup.fragments = {
  job: gql`
    ${InterviewGroupOld.fragments.job}
    ${JobStageInterview.fragments.job}
    fragment InterviewGroup_job on Job {
      id
      ...InterviewGroupOld_job
      ...JobStageInterview_job
    }
  `,
  jobStageInterviewGroup: gql`
    ${InterviewGroupOld.fragments.jobStageInterviewGroup}
    ${JobStageInterview.fragments.jobStageInterview}
    fragment InterviewGroup_jobStageInterviewGroup on JobStageInterviewGroup {
      id
      # index # Not using index in code, only using it while storing it.
      locked
      name
      jobStageInterviews {
        id
        ...JobStageInterview_jobStageInterview
      }
      ...InterviewGroupOld_jobStageInterviewGroup
    }
  `,
  jobStageInterviews: gql`
    ${InterviewGroupOld.fragments.jobStageInterviews}
    ${JobStageInterview.fragments.jobStageInterview}
    ${JobStageInterview.fragments.jobStageInterviews}
    ${JobStageInterview.fragments.linkableInterviews}
    fragment InterviewGroup_jobStageInterviews on JobStageInterview {
      id
      ...InterviewGroupOld_jobStageInterviews
      ...JobStageInterview_jobStageInterview
      ...JobStageInterview_jobStageInterviews
      ...JobStageInterview_linkableInterviews
    }
  `,
  jobStageInterviewGroups: gql`
    ${InterviewGroupOld.fragments.jobStageInterviewGroups}
    ${JobStageInterview.fragments.jobStageInterviewGroups}
    fragment InterviewGroup_jobStageInterviewGroups on JobStageInterviewGroup {
      id
      jobStageInterviews {
        id
      }
      ...InterviewGroupOld_jobStageInterviewGroups
      ...JobStageInterview_jobStageInterviewGroups
    }
  `,
  schedulingWindow: gql`
    ${JobStageInterview.fragments.schedulingWindow}
    fragment InterviewGroup_schedulingWindow on Duration {
      ...JobStageInterview_schedulingWindow
    }
  `,
};

export default InterviewGroup;
